import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from 'react';
import classnames from 'classnames';
import InputMask from 'react-input-mask';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Clock as ClockIcon } from 'lucide-react';
import { Input, Popover, PopoverContent, PopoverTrigger } from '@cargomatic/ui';
import { LocaleContext } from 'providers/locale-settings-context';

const twentyFourHourClockMask = [/[0-2]/, /[0-9]/, ':', /[0-5]/, /[0-9]/];
const twelveHourClockMask = [
  /[0-1]/,
  /[0-9]/,
  ':',
  /[0-5]/,
  /[0-9]/,
  ' ',
  /(a|A|p|P)/,
];

// Minutes between each minute-column option. 1 = every minute (the default);
// consumers can pass a coarser step (e.g. 15) to thin the column.
const DEFAULT_MINUTE_STEP = 1;
// Formats accepted when matching the current text value to the columns,
// so "4:30 pm" / "16:30" still highlights the right rows.
const MATCH_FORMATS = ['h:mm A', 'hh:mm A', 'H:mm', 'HH:mm'];

// Bumped per mounted instance so each TimePicker's option ids are unique
// (aria-activedescendant must point at a globally-unique id).
let timePickerIdCounter = 0;

const pad2 = (n) => String(n).padStart(2, '0');
const to24Hour = (hour12, meridiem) => {
  const h = hour12 % 12;
  return meridiem === 'PM' ? h + 12 : h;
};

export default function TimePicker({
  is24HourClock: is24HourClockProp,
  value,
  onChange,
  alwaysShowMask,
  disabled,
  className,
  dataTestId,
  timeIntervals,
}) {
  const localeContext = useContext(LocaleContext);
  const is24HourClockContext = localeContext?.localeData?.is24HourFormat;

  let is24HourClock;
  if (typeof is24HourClockProp !== 'undefined') {
    is24HourClock = is24HourClockProp;
  } else {
    is24HourClock = is24HourClockContext;
  }

  function getMask() {
    if (is24HourClock) {
      return [...twentyFourHourClockMask];
    }
    return [...twelveHourClockMask];
  }
  const [mask, setMask] = useState(getMask());
  const [open, setOpen] = useState(false);
  // Keyboard focus within the dropdown: which column, and the highlighted row
  // in that column (the aria-activedescendant target). -1 when none.
  const [focusedCol, setFocusedCol] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);

  // Wraps the field so we can refocus the masked input when the dropdown opens.
  const containerRef = useRef(null);
  // The scroll container DOM node for each column, by index.
  const colNodes = useRef([]);
  // Stable per-instance id prefix so option ids don't collide across pickers.
  const idRef = useRef(null);
  if (idRef.current === null) {
    timePickerIdCounter += 1;
    idRef.current = `timepicker-options-${timePickerIdCounter}`;
  }
  const optionsId = idRef.current;

  useEffect(() => {
    // Conditional mask for the 2nd digit base on the first digit
    if (is24HourClock) {
      if ((value || '').startsWith('2')) {
        setMask([
          twentyFourHourClockMask[0],
          /[0-3]/,
          ':',
          twentyFourHourClockMask[3],
          twentyFourHourClockMask[4],
        ]);
      } else {
        setMask([...twentyFourHourClockMask]);
      }
    } else if ((value || '').startsWith('1')) {
      setMask([
        twelveHourClockMask[0],
        /[0-2]/,
        ...twelveHourClockMask.slice(2),
      ]);
    } else {
      setMask([...twelveHourClockMask]);
    }
  }, [value]);

  function beforeMaskedStateChange({ nextState }) {
    if (is24HourClock) {
      return nextState;
    }

    let nextValue = nextState.value || '';

    if (nextValue.match(/(a|A|p|P)/)) {
      nextValue = nextValue.replace(/(a|A|p|P)(m|M)?/, '$1M').toUpperCase();
      return { ...nextState, value: nextValue };
    }
    return nextState;
  }

  function handleChange(e) {
    onChange(e);
  }

  const displayFormat = is24HourClock ? 'HH:mm' : 'hh:mm A';

  // Parse the current text value into hour/minute/meridiem; null when it isn't
  // yet a complete, valid time.
  const parsed = useMemo(() => {
    if (!value) return null;
    const m = moment(value, MATCH_FORMATS, true);
    if (!m.isValid()) return null;
    return {
      hour24: m.hour(),
      hour12: m.hour() % 12 || 12,
      minute: m.minute(),
      meridiem: m.hour() < 12 ? 'AM' : 'PM',
    };
  }, [value]);

  // Column option lists.
  const hourItems = useMemo(() => {
    const arr = [];
    if (is24HourClock) {
      for (let h = 0; h < 24; h += 1) arr.push({ value: h, label: pad2(h) });
    } else {
      for (let h = 1; h <= 12; h += 1) arr.push({ value: h, label: pad2(h) });
    }
    return arr;
  }, [is24HourClock]);

  const minuteItems = useMemo(() => {
    const step = timeIntervals > 0 ? timeIntervals : DEFAULT_MINUTE_STEP;
    const arr = [];
    for (let m = 0; m < 60; m += step) arr.push({ value: m, label: pad2(m) });
    return arr;
  }, [timeIntervals]);

  const meridiemItems = useMemo(
    () => [
      { value: 'AM', label: 'AM' },
      { value: 'PM', label: 'PM' },
    ],
    [],
  );

  // Emit the same event shape consumers read off the masked input.
  const emit = useCallback(
    (hour24, minute) => {
      const m = moment({ hour: hour24, minute });
      onChange({ target: { value: m.format(displayFormat) } });
    },
    [onChange, displayFormat],
  );

  // Selecting a part keeps the others (defaulting to 12:00 AM / 00:00 when the
  // field is empty) and re-emits the full time. The popover stays open so the
  // user can set hour, minute and meridiem in any order.
  const baseHour24 = parsed ? parsed.hour24 : 0;
  const baseHour12 = parsed ? parsed.hour12 : 12;
  const baseMinute = parsed ? parsed.minute : 0;
  const baseMeridiem = parsed ? parsed.meridiem : 'AM';

  const columns = useMemo(() => {
    const cols = [
      {
        key: 'hour',
        label: 'Hour',
        items: hourItems,
        selectedIndex: parsed
          ? hourItems.findIndex(
              (i) =>
                i.value === (is24HourClock ? parsed.hour24 : parsed.hour12),
            )
          : -1,
        onSelect: (h) =>
          emit(is24HourClock ? h : to24Hour(h, baseMeridiem), baseMinute),
      },
      {
        key: 'minute',
        label: 'Minute',
        items: minuteItems,
        selectedIndex: parsed
          ? minuteItems.findIndex((i) => i.value === parsed.minute)
          : -1,
        onSelect: (m) => emit(baseHour24, m),
      },
    ];
    if (!is24HourClock) {
      cols.push({
        key: 'meridiem',
        label: 'AM/PM',
        items: meridiemItems,
        selectedIndex: parsed ? (parsed.meridiem === 'PM' ? 1 : 0) : -1,
        onSelect: (mer) => emit(to24Hour(baseHour12, mer), baseMinute),
      });
    }
    return cols;
  }, [
    hourItems,
    minuteItems,
    meridiemItems,
    parsed,
    is24HourClock,
    emit,
    baseHour24,
    baseHour12,
    baseMinute,
    baseMeridiem,
  ]);

  // Refs mirrored for the identity-stable column ref-callbacks below.
  const openRef = useRef(open);
  openRef.current = open;
  const selectedIndexRef = useRef([]);
  selectedIndexRef.current = columns.map((c) => c.selectedIndex);

  // Center a column's selected row. scrollTop directly (columns are `relative`,
  // so a row's offsetTop is measured against the column); scrollIntoView is
  // unreliable on a just-mounted popover.
  const centerColumn = useCallback((i) => {
    const node = colNodes.current[i];
    const idx = selectedIndexRef.current[i];
    if (!node || idx == null || idx < 0) return;
    const el = node.querySelector(`[data-idx="${idx}"]`);
    if (el) {
      node.scrollTop = el.offsetTop - (node.clientHeight - el.offsetHeight) / 2;
    }
  }, []);

  // Center each column when it MOUNTS (Radix mounts popover content a render
  // after `open` flips, so an effect keyed on `open` would run too early).
  const bindColumn = useCallback(
    (i, node) => {
      const justMounted = node !== null && !colNodes.current[i];
      colNodes.current[i] = node;
      if (justMounted && openRef.current) centerColumn(i);
    },
    [centerColumn],
  );
  const colRef0 = useCallback((n) => bindColumn(0, n), [bindColumn]);
  const colRef1 = useCallback((n) => bindColumn(1, n), [bindColumn]);
  const colRef2 = useCallback((n) => bindColumn(2, n), [bindColumn]);
  const colRefs = [colRef0, colRef1, colRef2];

  // Reopen where Radix reused the persisted nodes (no remount) + seed keyboard
  // focus on the hour column.
  useEffect(() => {
    if (!open) {
      setActiveIndex(-1);
      return;
    }
    setFocusedCol(0);
    setActiveIndex(
      columns[0].selectedIndex >= 0 ? columns[0].selectedIndex : 0,
    );
    columns.forEach((_, i) => centerColumn(i));
    // Only re-run on open/close.
  }, [open]);

  // Keep the keyboard-highlighted row in the focused column in view.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const node = colNodes.current[focusedCol];
    if (!node) return;
    const el = node.querySelector(`[data-idx="${activeIndex}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [open, focusedCol, activeIndex]);

  // Per-column keyboard nav while focus stays in the masked input: Up/Down move
  // within a column, Left/Right switch columns, Enter selects, Escape closes.
  function handleKeyDown(e) {
    if (disabled) return;
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    const col = columns[focusedCol];
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) =>
          Math.min((i < 0 ? -1 : i) + 1, col.items.length - 1),
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case 'ArrowLeft':
      case 'ArrowRight': {
        e.preventDefault();
        const next =
          e.key === 'ArrowLeft'
            ? Math.max(0, focusedCol - 1)
            : Math.min(columns.length - 1, focusedCol + 1);
        setFocusedCol(next);
        setActiveIndex(
          columns[next].selectedIndex >= 0 ? columns[next].selectedIndex : 0,
        );
        break;
      }
      case 'Enter':
        if (activeIndex >= 0 && activeIndex < col.items.length) {
          e.preventDefault();
          col.onSelect(col.items[activeIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        break;
      default:
        break;
    }
  }

  return (
    <div ref={containerRef} className={classnames('relative', className)}>
      <Popover
        // modal opts the popover out of a parent Dialog's scroll lock so
        // wheel scrolling works inside the columns (same fix as
        // EquipmentCombobox). See https://github.com/dip/cmdk/issues/272
        modal
        open={open && !disabled}
        onOpenChange={setOpen}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Open time options"
            tabIndex={-1}
            disabled={disabled}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground z-10 disabled:opacity-50 disabled:cursor-default"
          >
            <ClockIcon className="w-4 h-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          // z-[1100] keeps the dropdown above legacy react-bootstrap modals
          // (z-1050); a default Radix popover (z-50) would render behind them.
          className="w-auto p-1 z-[1100]"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            // Keep focus on the masked input (not the popover content) so
            // arrow-key nav works even when opened by clicking the clock icon.
            const input = containerRef.current?.querySelector('input');
            if (input) input.focus();
          }}
          data-testid="TimePicker-options"
        >
          <div className="flex gap-1">
            {columns.map((col, ci) => (
              <div
                key={col.key}
                ref={colRefs[ci]}
                id={`${optionsId}-${ci}`}
                role="listbox"
                aria-label={col.label}
                data-col={col.key}
                className="relative max-h-56 w-14 overflow-y-auto"
              >
                {col.items.map((item, ri) => {
                  const isSelected = ri === col.selectedIndex;
                  const isActive =
                    open && focusedCol === ci && activeIndex === ri;
                  return (
                    <button
                      key={item.value}
                      id={`${optionsId}-${ci}-${ri}`}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      data-idx={ri}
                      data-selected={isSelected}
                      onClick={() => col.onSelect(item.value)}
                      className={classnames(
                        'block w-full rounded px-2 py-1 text-center text-sm tabular-nums hover:bg-accent/60',
                        {
                          'bg-accent text-accent-foreground font-medium':
                            isSelected,
                          'ring-1 ring-inset ring-ring':
                            isActive && !isSelected,
                        },
                      )}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <InputMask
        mask={mask}
        value={value}
        onChange={handleChange}
        alwaysShowMask={alwaysShowMask}
        beforeMaskedStateChange={beforeMaskedStateChange}
        disabled={disabled}
      >
        <Input
          type="text"
          data-testid={dataTestId}
          className="pr-9"
          role="combobox"
          aria-expanded={open && !disabled}
          aria-controls={`${optionsId}-0`}
          aria-autocomplete="list"
          aria-activedescendant={
            open && activeIndex >= 0
              ? `${optionsId}-${focusedCol}-${activeIndex}`
              : undefined
          }
          onKeyDown={handleKeyDown}
        />
      </InputMask>
    </div>
  );
}

TimePicker.propTypes = {
  is24HourClock: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  alwaysShowMask: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]),
  dataTestId: PropTypes.string,
  // Minutes between each minute-column option (1 = every minute).
  timeIntervals: PropTypes.number,
};

TimePicker.defaultProps = {
  alwaysShowMask: true,
  disabled: false,
  timeIntervals: DEFAULT_MINUTE_STEP,
};
